import { useMemo, useState } from 'react'
import { Filter, MessageSquarePlus, Search } from 'lucide-react'
import { Button, Select, Tabs, TextField } from '@/components/play'
import { FeedbackCreateModal } from '@/components/feedback/FeedbackCreateModal'
import { FeedbackList } from '@/components/feedback/FeedbackList'
import { RoadmapBoard } from '@/components/feedback/RoadmapBoard'
import { useFeedbackItems, useFeedbackRoadmap } from '@/hooks/useFeedback'
import type { FeedbackItemResource, FeedbackKind } from '@/types/feedback'

type FeedbackTab = 'all' | 'mine' | 'roadmap'
type StatusFilter = 'all' | 'draft' | 'open' | 'planned' | 'in_progress' | 'done' | 'closed'
type SortOrder = 'recent' | 'most_voted' | 'oldest'

const kindOptions: { value: FeedbackKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'bug', label: 'Bugs' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'feature_request', label: 'Feature requests' },
  { value: 'feature', label: 'Features' },
]

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'closed', label: 'Closed' },
]

const sortOptions: { value: SortOrder; label: string }[] = [
  { value: 'recent', label: 'Recently updated' },
  { value: 'most_voted', label: 'Most voted' },
  { value: 'oldest', label: 'Oldest' },
]

export function FeedbackPage() {
  const [tab, setTab] = useState<FeedbackTab>('all')
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<FeedbackKind | 'all'>('all')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [sort, setSort] = useState<SortOrder>('recent')
  const [createOpen, setCreateOpen] = useState(false)

  const filters = useMemo(() => ({
    query: query.trim() || undefined,
    kind,
    status,
    sort,
    reporter: tab === 'mine' ? 'me' as const : undefined,
  }), [kind, query, sort, status, tab])

  const { items, loading, error, setItems } = useFeedbackItems(filters)
  const roadmap = useFeedbackRoadmap()

  function handleCreated(item: FeedbackItemResource) {
    setItems((current) => [item, ...current])
  }

  const list = <FeedbackList items={items} loading={loading} error={error} />

  return (
    <div className="min-h-screen bg-transparent">
      <div className="app-page max-w-7xl space-y-8">
        {/* On the canvas, not in Cards: the feedback items are the cards. */}
        <header className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <h1 className="text-4xl font-semibold text-neutral-10 md:text-5xl">Feedback</h1>
            <p className="max-w-2xl text-base text-neutral-7">Report a bug, ask for a feature and see what’s next.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <MessageSquarePlus aria-hidden="true" />
            Share feedback
          </Button>
        </header>

        <Tabs
          label="Feedback"
          fill
          value={tab}
          onValueChange={setTab}
          actions={tab !== 'roadmap' ? (
            <div className="grid w-full gap-3 lg:w-auto lg:grid-cols-[minmax(220px,1fr)_180px_180px_180px]">
              <TextField
                type="search"
                label="Search feedback"
                hideLabel
                icon={<Search />}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search feedback"
              />
              <Select label="Type" hideLabel icon={<Filter />} items={kindOptions} value={kind} onValueChange={(value) => { if (value) setKind(value) }} />
              <Select label="Status" hideLabel items={statusOptions} value={status} onValueChange={(value) => { if (value) setStatus(value) }} />
              <Select label="Sort" hideLabel items={sortOptions} value={sort} onValueChange={(value) => { if (value) setSort(value) }} />
            </div>
          ) : null}
          items={[
            { value: 'all', label: 'All', content: list },
            { value: 'mine', label: 'My tickets', content: list },
            { value: 'roadmap', label: 'Roadmap', content: <RoadmapBoard items={roadmap.items} /> },
          ]}
        />
      </div>

      <FeedbackCreateModal open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreated} />
    </div>
  )
}
